import { AxiosResponse } from 'axios'
import { QueryKey } from '@tanstack/react-query'
import PaginatedResponse from '@/types/paginated-response'
import { Filters, RequestOptions } from '../request-types'
import useInfinitePaginatedQuery, {
  UseInfinitePaginationQueryOptions,
} from './useInfinitePaginationQuery'

export type UseGetListOptions<T> = Omit<
  UseInfinitePaginationQueryOptions<PaginatedResponse<T>>,
  'queryKey'
> &
  RequestOptions & {
    /** If you have initial data that's an array, use this instead of initialData. */
    initialListData?: T[]
    queryKey?: QueryKey
  }

type ServiceFn<T> = {
  /** This is the function that makes the request. This should come from the respective service. */
  serviceFn: (
    options: RequestOptions,
  ) => Promise<AxiosResponse<PaginatedResponse<T>>>
}

/**
 * This hook fetches a list of items from the backend.
 */
export default function useGetList<T>({
  queryKey,
  serviceFn,
  filters,
  axiosOptions,
  initialListData,
  paginator = 'cursor',
  ...options
}: UseGetListOptions<T> & ServiceFn<T> & { queryKey: QueryKey }) {
  const query = useInfinitePaginatedQuery<T>({
    queryKey,
    queryFn: ({ pageParam, signal }) => {
      const fullFilters: Filters = { ...filters, paginator }
      if (pageParam) fullFilters[paginator] = pageParam
      return serviceFn({
        filters: fullFilters,
        axiosOptions: { signal, ...axiosOptions },
      }).then(({ data }) => data)
    },
    paginator,
    initialData: initialListData
      ? {
          pageParams: [],
          pages: [
            {
              results: initialListData,
              next: null,
              previous: null,
            },
          ],
        }
      : undefined,
    ...options,
  } as UseInfinitePaginationQueryOptions<PaginatedResponse<T>>)

  return {
    ...query,
    queryKey,
  }
}
